import type { WebSocketStatus } from '@vueuse/core';
import { useWeb3ModalAccount } from '@web3modal/ethers/vue';
import { ZeroAddress } from 'ethers';
import { WebSocketsKeys } from '~/types/common';
import { GameStatus } from '~/types/FlipCoin';
import { apiUrls } from '~/utils/constants';

export default defineNuxtPlugin({
  dependsOn: ['web3-modal'],
  hooks: {
    'app:mounted': () => {
      const nuxtApp = useNuxtApp();
      const { wsUrl } = useEnvs();
      const { address } = useWeb3ModalAccount();

      const activeGamesStatuses = [GameStatus.INITIAL, GameStatus.PENDING, GameStatus.STARTED];
      const statusesQuery = activeGamesStatuses.join(',');

      const endpoints = computed(() => [
        {
          key: WebSocketsKeys.streetMerchant,
          url: `${wsUrl}${apiUrls.ws.streetMerchant}`,
          parse: (msg: MessageEvent) => JSON.parse(msg.data)
        },
        {
          key: WebSocketsKeys.flipCoinGame,
          url: `${wsUrl}/ws/flip-coin-game?status=${statusesQuery}`,
          parse: (msg: MessageEvent) => JSON.parse(msg.data)
        },
        {
          key: WebSocketsKeys.craftingActivity,
          url: `${wsUrl}${apiUrls.ws.craftingActivity(address.value || ZeroAddress)}`,
          parse: (msg: MessageEvent) => JSON.parse(msg.data)
        }
      ]);

      const sockets = reactive<Record<WebSocketsKeys, { parsedData: any; status: WebSocketStatus }>>(
        endpoints.value.reduce(
          (acc, { key }) => {
            acc[key] = {
              parsedData: null,
              status: 'CLOSED'
            };
            return acc;
          },
          {} as Record<string, { parsedData: any; status: WebSocketStatus }>
        )
      );

      const autoReconnect = {
        retries: 3,
        delay: 1000
      };

      for (const { key, parse } of endpoints.value) {
        if (!endpoints.value.find((e) => e.key === key)?.url) {
          continue;
        }
        const { status, close, open } = useWebSocket(() => endpoints.value.find((e) => e.key === key)?.url!, {
          autoReconnect: {
            ...autoReconnect,
            onFailed() {
              console.log(`Failed to connect ${key} WebSocket after 3 retries`);
            }
          },
          onMessage: (ws, event) => {
            try {
              sockets[key].parsedData = parse(event);
            } catch (err) {
              console.error(`Error parsing message from ${key}`, err);
            }
          }
        });
        watch(endpoints, (newEndpoints, oldEndpoints) => {
          if (newEndpoints.find((e) => e.key === key)?.url !== oldEndpoints.find((e) => e.key === key)?.url) {
            close();
          }
          if (newEndpoints.find((e) => e.key === key)?.url) {
            open();
          }
        });

        watchEffect(() => {
          if (!sockets[key]) {
            return;
          }
          sockets[key].status = status.value;
        });
      }

      nuxtApp.provide('websockets', readonly(sockets));
    }
  }
});
